import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { showErrorToast, showSuccessToast } from "../../components/Toast";
import { ConfirmDialog } from "../../components/SyDialog";
import { getAuthToken } from "../../lib/api/tokenStore";
import usePayoutDetail from "./usePayoutDetail";
import FinanceShell from "./components/FinanceShell";
import StatusPill from "./components/StatusPill";
import FinanceEmptyState from "./components/FinanceEmptyState";
import { SummaryLinesSkeleton } from "./components/FinanceSkeletons";
import { FINANCE_COPY, FINANCE_EMPTY_STATES, serverMessage } from "./financeCopy";
import { formatMoney } from "./financeMoney";
import { formatDateAndTime } from "../orders/format";
import { WITHDRAWALS_PATH } from "./financeRoutes";

import "../checkout/styles.css";
import "./styles.css";

const IMAGE_FILE = /\.(png|jpe?g|gif|webp|avif)(\?|#|$)/i;

const DetailRow = ({ label, value }) =>
  value === null || value === undefined || value === "" ? null : (
    <div className="fin-detail-row">
      <span className="fin-detail-label">{label}</span>
      <span className="fin-detail-value">{value}</span>
    </div>
  );

const WithdrawalDetailRoute = () => {
  const { payoutId } = useParams();
  const enabled = Boolean(getAuthToken());

  const [confirming, setConfirming] = useState(false);
  const detail = usePayoutDetail({ payoutId, enabled });
  const { payout } = detail;

  const handleCancel = async () => {
    const outcome = await detail.cancel();

    setConfirming(false);

    if (outcome.status === "error") {
      showErrorToast(serverMessage(outcome.error, FINANCE_COPY.cancelWithdrawalFailed));

      return;
    }

    showSuccessToast("This withdrawal request has been cancelled.", "Withdrawal Cancelled");
  };

  return (
    <FinanceShell
      title="Withdrawal Request"
      backTo={WITHDRAWALS_PATH}
    >
      {detail.isLoading ? (
        <section className="sy-flow-card">
          <div className="sy-flow-card-body">
            <SummaryLinesSkeleton lines={6} />
          </div>
        </section>
      ) : !payout ? (
        <FinanceEmptyState
          state={FINANCE_EMPTY_STATES.noWithdrawals}
          onRetry={detail.refetch}
        />
      ) : (
        <>
          <section className="sy-flow-card">
            <div className="sy-flow-card-head">
              <h2 className="sy-flow-card-title">{formatMoney(payout.amount)}</h2>
              <StatusPill status={payout.status} label={payout.statusLabel} />
            </div>

            <div className="sy-flow-card-body">
              <div className="fin-detail-rows">
                <DetailRow label="Reference" value={`#${payout.reference}`} />
                <DetailRow
                  label="Fee"
                  value={payout.feeAmount ? formatMoney(payout.feeAmount) : null}
                />
                <DetailRow
                  label="You receive"
                  value={payout.netAmount ? formatMoney(payout.netAmount) : null}
                />
                <DetailRow label="Account" value={payout.recipientLabel} />
                <DetailRow
                  label="Requested"
                  value={payout.requestedAt ? formatDateAndTime(payout.requestedAt) : null}
                />
                <DetailRow
                  label="Approved"
                  value={payout.approvedAt ? formatDateAndTime(payout.approvedAt) : null}
                />
                <DetailRow
                  label="Paid"
                  value={payout.paidAt ? formatDateAndTime(payout.paidAt) : null}
                />
                <DetailRow
                  label="Rejected"
                  value={payout.rejectedAt ? formatDateAndTime(payout.rejectedAt) : null}
                />
              </div>

              {payout.rejectionReason && (
                <p className="fin-detail-reason">{payout.rejectionReason}</p>
              )}
            </div>
          </section>

          {payout.receiptUrl && (
            <section className="sy-flow-card">
              <div className="sy-flow-card-head">
                <h2 className="sy-flow-card-title">Receipt</h2>
              </div>

              <div className="sy-flow-card-body">
                {IMAGE_FILE.test(payout.receiptUrl) ? (
                  <a
                    className="fin-receipt"
                    href={payout.receiptUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={payout.receiptUrl} alt="Withdrawal receipt" />
                  </a>
                ) : (
                  <a
                    className="sy-flow-btn sy-flow-btn-ghost"
                    href={payout.receiptUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open Receipt
                  </a>
                )}
              </div>
            </section>
          )}

          {payout.canCancel && (
            <div className="fin-sticky-bar">
              <button
                type="button"
                className="sy-flow-btn sy-flow-btn-danger"
                disabled={detail.cancelling}
                onClick={() => setConfirming(true)}
              >
                {detail.cancelling ? "Cancelling…" : "Cancel Request"}
              </button>
            </div>
          )}
        </>
      )}

      {confirming && (
        <ConfirmDialog
          title="Cancel this withdrawal?"
          message="The money stays in your wallet and this request will be closed."
          confirmText="Cancel Request"
          cancelText="Keep It"
          busy={detail.cancelling}
          onConfirm={handleCancel}
          onCancel={() => setConfirming(false)}
        />
      )}
    </FinanceShell>
  );
};

export default WithdrawalDetailRoute;
